import { useState, useEffect, useCallback } from "react";
import { ToastProvider } from "./components/Toast";
import { CookieConsent } from "./components/CookieConsent";
import { SignInPrompt } from "./components/SignInPrompt";
import { Onboarding, needsOnboarding } from "./components/Onboarding";
import { SplashVideo, needsSplash } from "./components/SplashVideo";
import { Home } from "./pages/Home";
import { DailyChallenge } from "./pages/DailyChallenge";
import { HeadToHead } from "./pages/HeadToHead";
import { PlaylistBrowser } from "./pages/PlaylistBrowser";
import { Profile } from "./pages/Profile";
import { Achievements } from "./pages/Achievements";
import { PartyMode } from "./pages/PartyMode";
import { HowToPlay } from "./pages/HowToPlay";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Terms } from "./pages/Terms";
import { Accessibility } from "./pages/Accessibility";
import { BottomTabV2, type V2TabId } from "./components/v2";
import { LogoV2 } from "./components/v2";
import { HeaderMenu } from "./components/HeaderMenu";
import { isNativePlatform } from "./lib/platform";
import { OfflineBanner } from "./components/OfflineBanner";
import { UpdateToast } from "./components/UpdateToast";
import { InstallBanner } from "./components/InstallBanner";
import { NotificationBanner } from "./components/NotificationBanner";
import { useAuth } from "./hooks/useAuth";
import { featureFlags } from "./lib/featureFlags";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useInstallPrompt } from "./hooks/useInstallPrompt";
import { useNotificationPermission } from "./hooks/useNotificationPermission";
import { getStats } from "./lib/storage";
import { syncPendingScores, getPlayerStats } from "./lib/scores";
import { syncPlayedSongs } from "./lib/playedSongs";
import { createChallenge } from "./lib/h2h";
import { useLocale } from "./lib/i18n";

declare global {
  interface Window {
    __h2hJoinCode?: string;
    __h2hChallengerCode?: string;
    __partyJoinCode?: string;
  }
}

export default function App() {
  const { locale, setLocale, isRTL, t } = useLocale();
  const [activeTab, setActiveTab] = useState<"home" | "daily" | "praktis" | "h2h" | "party" | "uncover" | "profile" | "achievements" | "privacy" | "terms" | "accessibility">("home");
  const [showHelp, setShowHelp] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [pendingPlaylistId, setPendingPlaylistId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(needsOnboarding());
  const hasDeepLink = /^#(h2h\/|party\/|share\?)/.test(window.location.hash);
  const [showSplash, setShowSplash] = useState(!hasDeepLink && needsSplash());
  const [shareLanding, setShareLanding] = useState<{ dailyNumber: string; stage: number; name: string } | null>(null);
  const [swNeedsUpdate, setSwNeedsUpdate] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [stats, setStats] = useState(getStats);
  const auth = useAuth();
  const online = useOnlineStatus();
  const { showBanner: showInstall, isIOS, install, dismiss: dismissInstall, autoHide, checkShouldShow } = useInstallPrompt();

  const isInstalled = window.matchMedia("(display-mode: standalone)").matches;
  const GAMES_AFTER_INSTALL_KEY = "nigunim:gamesAfterInstall";
  const gamesAfterInstall = isInstalled
    ? parseInt(localStorage.getItem(GAMES_AFTER_INSTALL_KEY) || "0", 10)
    : 0;
  const { showBanner: showNotifBanner, dismiss: dismissNotif, onSubscribed } = useNotificationPermission(isInstalled, gamesAfterInstall);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").then((reg) => {
        setSwRegistration(reg);
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (newSW) {
            newSW.addEventListener("statechange", () => {
              if (newSW.state === "installed" && navigator.serviceWorker.controller) {
                setSwNeedsUpdate(true);
              }
            });
          }
        });
      });
    }

    // Deep links
    const hash = window.location.hash;

    // H2H deep link (feature-flag gated; v1 off by default)
    if (featureFlags.h2h()) {
      const h2hMatch = hash.match(/^#h2h\/([A-Z0-9]{6})/i);
      if (h2hMatch) {
        setActiveTab("h2h");
        window.__h2hJoinCode = h2hMatch[1].toUpperCase();
        window.location.hash = "#";
      }
    }

    // Party deep link (feature-flag gated; v1 off by default)
    if (featureFlags.party()) {
      const partyMatch = hash.match(/^#party\/([A-Z0-9]{4,8})/i);
      if (partyMatch) {
        setActiveTab("party");
        window.__partyJoinCode = partyMatch[1].toUpperCase();
        window.location.hash = "#";
      }
    }

    // Share deep link: #share?d=...&s=...&n=...
    const shareMatch = hash.match(/^#share\?/);
    if (shareMatch) {
      const shareParams = new URLSearchParams(hash.replace("#share?", ""));
      setShareLanding({
        dailyNumber: shareParams.get("d") ?? "?",
        stage: parseInt(shareParams.get("s") ?? "0", 10),
        name: decodeURIComponent(shareParams.get("n") ?? "Someone"),
      });
      window.location.hash = "#";
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      syncPendingScores();
      syncPlayedSongs();
    };
    window.addEventListener("online", handleOnline);
    syncPendingScores();
    syncPlayedSongs();
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  useEffect(() => {
    checkShouldShow();
    setStats(getStats());
  }, [activeTab, checkShouldShow]);

  // When the user's auth state changes (sign-in / sign-out), refresh stats from
  // Supabase so the header streak reflects their real server-side count even
  // if localStorage on this device is empty (e.g. fresh install, logged in on
  // another device, cleared site data). Guests still get the localStorage path.
  useEffect(() => {
    if (!auth.user) return;
    getPlayerStats().then(setStats).catch(() => { /* best-effort */ });
  }, [auth.user]);

  const handleSwRefresh = useCallback(() => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    window.location.reload();
  }, [swRegistration]);

  const handleSplashDone = useCallback(() => setShowSplash(false), []);

  if (showSplash) {
    return <SplashVideo onDone={handleSplashDone} />;
  }

  if (shareLanding) {
    const { dailyNumber, stage, name } = shareLanding;
    const resultText = stage > 0
      ? t("shareLanding.gotIt", { name, stage })
      : t("shareLanding.failed", { name });

    return (
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="relative w-full max-w-[430px] mx-auto min-h-[100dvh] flex flex-col items-center justify-center px-6 lg:my-4 lg:min-h-[calc(100dvh-2rem)] lg:rounded-3xl lg:shadow-2xl lg:border lg:border-white/10 overflow-hidden"
        style={{ background: "var(--bg-ambient)" }}
      >
        <h1
          className="font-serif tracking-wider mb-8"
          style={{ color: "var(--gold-400)", fontSize: 36, fontWeight: 600 }}
        >
          ניגונים
        </h1>

        <div className="w-full bg-white/5 border border-white/10 backdrop-blur-md rounded-2xl p-6 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">
            {t("daily.title", { num: dailyNumber })}
          </p>
          <p className="text-xl font-bold text-white mb-2">{resultText}</p>
          <p className="text-sm text-zinc-400">{t("shareLanding.beatThat")}</p>
        </div>

        <button
          onClick={async () => {
            setShareLanding(null);
            // Auto-create a 1-song H2H challenge and jump straight in
            const result = await createChallenge({ songCount: 1 });
            if (result) {
              window.__h2hChallengerCode = result.code;
              setActiveTab("h2h");
            } else {
              // Not signed in or failed — fall back to H2H tab
              setActiveTab("h2h");
            }
          }}
          className="btn-primary w-full py-4 rounded-2xl font-bold text-lg mt-6 flex items-center justify-center gap-2"
        >
          {t("shareLanding.challengeBack")}
        </button>

        <button
          onClick={() => { setShareLanding(null); setActiveTab("daily"); }}
          className="w-full py-3 rounded-2xl font-bold text-sm mt-3 glass text-white flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
        >
          {t("shareLanding.playToday")}
        </button>

        <button
          onClick={() => { setShareLanding(null); setShowHelp(true); }}
          className="mt-4 text-sm text-zinc-400 hover:text-white transition-colors"
        >
          {t("shareLanding.howDoesItWork")}
        </button>
      </div>
    );
  }

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} skipSplash />;
  }

  return (
    <ToastProvider>
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="relative w-full max-w-[430px] mx-auto h-[100dvh] flex flex-col overflow-hidden lg:shadow-2xl lg:border lg:border-white/10"
      style={{ background: "var(--bg-ambient)" }}
    >
      <OfflineBanner online={online} />
      <header
        className="pb-2 px-5 flex items-center justify-between"
        style={{
          paddingTop: isNativePlatform()
            ? "calc(env(safe-area-inset-top) + 16px)"
            : "max(0.75rem, env(safe-area-inset-top))",
        }}
      >
        <button onClick={() => setActiveTab("home")} className="appearance-none cursor-pointer">
          <LogoV2 size={18} />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLocale(locale === "he" ? "en" : "he")}
            aria-label="Toggle language"
            className="px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums transition-opacity active:opacity-60"
            style={{
              fontFamily: "'Heebo', sans-serif",
              color: "var(--gold-300)",
              background: "rgba(212, 162, 74, 0.08)",
              border: "1px solid rgba(212, 162, 74, 0.32)",
              letterSpacing: "0.08em",
            }}
          >
            {locale === "he" ? "EN" : "עב"}
          </button>
          <HeaderMenu
            streak={stats.currentStreak}
            locale={locale}
            setLocale={setLocale}
            isLoggedIn={!!auth.user}
            displayName={auth.displayName}
            onSignIn={() => setShowSignIn(true)}
            onSignOut={auth.signOut}
            onProfile={() => { setActiveTab("profile"); setShowHelp(false); }}
            onHelp={() => setShowHelp(true)}
          />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        {showHelp ? (
          <div className="relative">
            <button
              onClick={() => setShowHelp(false)}
              className="absolute top-4 end-5 z-10 w-8 h-8 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-white text-lg font-bold transition-colors"
            >
              ✕
            </button>
            <HowToPlay />
          </div>
        ) : (
          <>
            {activeTab === "home" && (
              <Home
                onPick={(tab) => setActiveTab(tab)}
                onOpenPlaylist={(id) => {
                  setPendingPlaylistId(id);
                  setActiveTab("praktis");
                }}
              />
            )}
            {activeTab === "daily" && <DailyChallenge onNavigate={(tab) => { setActiveTab(tab as any); }} />}
            {activeTab === "praktis" && (
              <PlaylistBrowser
                onNavigate={(tab) => setActiveTab(tab as any)}
                initialPlaylistId={pendingPlaylistId}
                onInitialPlaylistConsumed={() => setPendingPlaylistId(null)}
              />
            )}
            {featureFlags.h2h() && activeTab === "h2h" && <HeadToHead onNavigate={(tab) => setActiveTab(tab as any)} />}
            {featureFlags.party() && activeTab === "party" && <PartyMode onNavigate={(tab) => setActiveTab(tab as any)} />}
            {activeTab === "achievements" && <Achievements />}
            {activeTab === "profile" && <Profile onNavigate={(p) => setActiveTab(p as any)} />}
            {activeTab === "privacy" && <PrivacyPolicy onBack={() => setActiveTab("home")} />}
            {activeTab === "terms" && <Terms onBack={() => setActiveTab("home")} onNavigate={(p) => setActiveTab(p as any)} />}
            {activeTab === "accessibility" && <Accessibility onBack={() => setActiveTab("home")} />}
          </>
        )}
      </main>

      {!["privacy", "terms", "accessibility"].includes(activeTab) && (
        <BottomTabV2
          active={
            activeTab === "praktis" ? "lists"
            : activeTab === "daily" ? "play"
            : activeTab === "profile" || activeTab === "achievements" ? "profile"
            : "home"
          }
          labels={{
            home: t("nav.home"),
            play: t("nav.play"),
            lists: t("nav.lists"),
            profile: t("nav.profile"),
          }}
          onChange={(tab: V2TabId) => {
            const next =
              tab === "lists" ? "praktis"
              : tab === "play" ? "daily"
              : tab === "profile" ? "profile"
              : "home";
            setActiveTab(next);
            setShowHelp(false);
          }}
        />
      )}

      <InstallBanner show={showInstall} isIOS={isIOS} streak={stats.currentStreak} onInstall={install} onDismiss={dismissInstall} onAutoHide={autoHide} />
      <NotificationBanner show={showNotifBanner} onDismiss={dismissNotif} onSubscribed={onSubscribed} />
      <UpdateToast show={swNeedsUpdate} onRefresh={handleSwRefresh} />
      <SignInPrompt
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        onGoogle={auth.signInWithGoogle}
        onApple={auth.signInWithApple}
      />
      <CookieConsent />
    </div>
    </ToastProvider>
  );
}
